import React, { useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AdminAuthContext } from "../../AdminAuthContext";
import { FiAlertTriangle, FiArrowLeft, FiCheckCircle, FiFileText, FiImage, FiInfo, FiSave } from 'react-icons/fi';
import ProductFeaturesEditor from '../../admin_components/ProductFeaturesEditor';
import './AdminUI.css';

const AdminAdd = () => {
  const navigate = useNavigate();
  const { admin, loading } = useContext(AdminAuthContext);
  const [formData, setFormData] = useState({
    product_name: "",
    product_description: "",
    product_price: "",
    product_brand: "",
    product_category: "",
    product_stock: "",
    product_rank: "",
    published: true,
  });
  const [productImages, setProductImages] = useState([]);
  const [productPdf, setProductPdf] = useState(null);
  const [productPdf2, setProductPdf2] = useState(null);
  const [productPdf3, setProductPdf3] = useState(null);
  const [productFeatures, setProductFeatures] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // State updates are asynchronous, so use a synchronous lock as well to
    // prevent two rapid submit events from creating duplicate products.
    if (submitLockRef.current) return;

    setError("");
    setSuccess("");
    const token = localStorage.getItem("admin_token");

    if (!token || !admin || admin.role !== "admin") {
      setError("You must be logged in as an administrator to add products.");
      navigate("/admin-login");
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);

    const data = new FormData();
    Object.keys(formData).forEach((key) => data.append(key, formData[key]));
    data.append('product_features', JSON.stringify(productFeatures));
    productImages.forEach((image) => data.append("product_images", image));
    if (productPdf) data.append("product_pdf", productPdf);
    if (productPdf2) data.append("product_pdf2", productPdf2);
    if (productPdf3) data.append("product_pdf3", productPdf3);

    try {
      const response = await fetch("https://valleyapi-dr9y.onrender.com/api/products", {
        method: "POST",
        body: data,
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await response.json();
      if (!response.ok) {
        if (result.error?.includes('Product name must be unique')) throw new Error('Product name is already in use.');
        throw new Error(result.error || "Failed to add product");
      }
      setSuccess("Product added successfully. Redirecting to the catalog...");
      setTimeout(() => navigate("/admin/products"), 1500);
    } catch (submitError) {
      console.error("AdminAdd: Error:", submitError.message);
      setError(submitError.message);
      submitLockRef.current = false;
      setSubmitting(false);
    }
  };

  if (loading) return <div className="va-page va-card va-loading"><div className="va-spinner" aria-label="Loading form" /></div>;

  return (
    <div className="va-page">
      <header className="va-page-header">
        <div>
          <span className="va-eyebrow">Catalog management</span>
          <h1 className="va-title">Add product</h1>
          <p className="va-subtitle">Create a complete catalog record with commercial details, media and documents.</p>
        </div>
        <button className="va-button va-button--secondary" type="button" onClick={() => navigate('/admin/products')}><FiArrowLeft /> Back to products</button>
      </header>

      {error && <div className="va-alert" role="alert"><FiAlertTriangle /> {error}</div>}
      {success && <div className="va-alert va-alert--success" role="status"><FiCheckCircle /> {success}</div>}

      <form onSubmit={handleSubmit}>
        <div className="va-detail-grid">
          <section className="va-card">
            <div className="va-card__header"><div><h2>Product information</h2><p>Core details customers will use to identify the product.</p></div><span className="va-stat__icon"><FiInfo /></span></div>
            <div className="va-card__body">
              <div className="va-form-grid">
                <div className="va-field va-field--full"><label htmlFor="product-name">Product name <span>Required</span></label><input id="product-name" className="va-input" type="text" name="product_name" value={formData.product_name} onChange={handleInputChange} placeholder="e.g. Siemens S7-1200 CPU 1214C" required /></div>
                <div className="va-field"><label htmlFor="product-brand">Brand <span>Required</span></label><input id="product-brand" className="va-input" type="text" name="product_brand" value={formData.product_brand} onChange={handleInputChange} placeholder="Manufacturer or brand" required /></div>
                <div className="va-field"><label htmlFor="product-category">Category <span>Required</span></label><input id="product-category" className="va-input" type="text" name="product_category" value={formData.product_category} onChange={handleInputChange} placeholder="Product category" required /></div>
                <div className="va-field"><label htmlFor="product-price">Price</label><input id="product-price" className="va-input" type="number" name="product_price" value={formData.product_price} onChange={handleInputChange} step="0.01" min="0" placeholder="0.00" /></div>
                <div className="va-field"><label htmlFor="product-stock">Stock</label><input id="product-stock" className="va-input" type="number" name="product_stock" value={formData.product_stock} onChange={handleInputChange} min="0" placeholder="0" /></div>
                <div className="va-field"><label htmlFor="product-rank">Display rank</label><input id="product-rank" className="va-input" type="number" name="product_rank" value={formData.product_rank} onChange={handleInputChange} min="0" placeholder="Optional priority" /><small>Lower numbers can be used to prioritize the item.</small></div>
                <div className="va-field va-field--full"><label htmlFor="product-description">Description <span>Required</span></label><textarea id="product-description" className="va-textarea" name="product_description" value={formData.product_description} onChange={handleInputChange} placeholder="Describe the product, specifications and typical applications." required /></div>
              </div>
            </div>
          </section>

          <div>
            <section className="va-card">
              <div className="va-card__header"><div><h2>Media</h2><p>Product gallery and technical documents.</p></div><span className="va-stat__icon"><FiImage /></span></div>
              <div className="va-card__body">
                <div className="va-form-grid va-form-grid--single">
                  <div className="va-field"><label htmlFor="product-images">Product images</label><input id="product-images" className="va-input" type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={(event) => setProductImages(Array.from(event.target.files || []).slice(0, 8))} /><small>{productImages.length ? `${productImages.length} image${productImages.length === 1 ? '' : 's'} selected: ${productImages.map((image) => image.name).join(', ')}` : 'Select up to 8 JPG, PNG or WebP images. The first image will be the cover.'}</small></div>
                  {[{ id: 'pdf-1', label: 'Technical document 1', set: setProductPdf, file: productPdf }, { id: 'pdf-2', label: 'Technical document 2', set: setProductPdf2, file: productPdf2 }, { id: 'pdf-3', label: 'Technical document 3', set: setProductPdf3, file: productPdf3 }].map((document) => (
                    <div className="va-field" key={document.id}><label htmlFor={document.id}><FiFileText style={{ verticalAlign: 'middle' }} /> {document.label}</label><input id={document.id} className="va-input" type="file" accept=".pdf" onChange={(event) => document.set(event.target.files[0])} /><small>{document.file ? document.file.name : 'PDF, optional.'}</small></div>
                  ))}
                </div>
              </div>
            </section>

            <section className="va-card">
              <div className="va-card__header"><div><h2>Publication</h2><p>Control catalog visibility.</p></div></div>
              <div className="va-card__body">
                <label className="va-check"><input type="checkbox" name="published" checked={formData.published} onChange={handleInputChange} /><span><strong>Publish immediately</strong><span>Show this product in public search results as soon as it is created.</span></span></label>
              </div>
            </section>
          </div>

          <section className="va-card va-product-features-card">
            <div className="va-card__header"><div><h2>Highlighted functions</h2><p>Optional icon-and-text functions shown only on this product page.</p></div></div>
            <div className="va-card__body"><ProductFeaturesEditor features={productFeatures} onChange={setProductFeatures} /></div>
          </section>
        </div>

        <div className="va-form-actions">
          <button type="button" className="va-button va-button--secondary" onClick={() => navigate('/admin/products')}>Cancel</button>
          <button type="submit" className="va-button" disabled={submitting}><FiSave /> {submitting ? 'Creating product...' : 'Create product'}</button>
        </div>
      </form>
    </div>
  );
};

export default AdminAdd;
